import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  Box, 
  Paper, 
  Typography,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Grid,
  DialogContent,
  InputAdornment,
  DialogActions
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import { getItems} from './action.ts';
import { toast } from 'react-toastify';

interface ItemDetailsFormProps {
  onSubmit: (data: any) => void;
  onBack: () => void;
  orderData: any;
  onClose: () => void;
  onCancel:()=> void;
  items: Item[];
  setItems: React.Dispatch<React.SetStateAction<Item[]>>;
  currentItem: Omit<Item, 'id'>;
  setCurrentItem: React.Dispatch<React.SetStateAction<Omit<Item, 'id'>>>;
  showGst: boolean;
  setShowGst: React.Dispatch<React.SetStateAction<boolean>>;
  weight: string;
  setWeight: React.Dispatch<React.SetStateAction<string>>;
  crates: string;
  setCrates: React.Dispatch<React.SetStateAction<string>>;
  cod: string;
  setCod: React.Dispatch<React.SetStateAction<string>>;
  deliveryType: string;
  setDeliveryType: React.Dispatch<React.SetStateAction<string>>;
  logisticsCost: string;
  setLogisticsCost: React.Dispatch<React.SetStateAction<string>>;
    grandTotal:string;
    setGrandTotal: React.Dispatch<React.SetStateAction<string>>;
  dimensions: {
    length: string;
    breadth: string;
    height: string;
  };
  setDimensions: React.Dispatch<React.SetStateAction<{
    length: string;
    breadth: string;
    height: string;
  }>>;
}

interface ErrorParams {
  hsnCode: string;
  igstDetails: string;
  sgstDetails: string;
  cgstDetails: string;
  discount: string;
  weight: string;
  length: string;
  breadth: string;
  height: string;
  cod: string;
  logistics_cost: string;
}

interface Item {
  id: string;
  skuId: string;
  item: string;
  description: string;
  rate: string;
  quantity: string;
  total: string;
  hsnCode?: string;
  igst?: string;
  cgst?: string;
  sgst?: string;
  discount?: string;
}

const ItemDetailsForm: React.FC<ItemDetailsFormProps> = ({ 
  onSubmit, 
  onBack, 
  orderData,
  onClose, 
  onCancel,
  items,
  setItems,
  currentItem,
  setCurrentItem,
  showGst,
  setShowGst,
  weight,
  setWeight,
  crates,
  setCrates,
  cod,
  setCod,
  deliveryType,
  setDeliveryType,
  logisticsCost,
  setLogisticsCost,
  grandTotal,
  setGrandTotal,
  dimensions,
  setDimensions 
}) => {
  
  const [fieldError, setFieldError] = useState<string>("");
  const [gstError, setGstError] = useState<string | null>(null);
  const [lengthError, setLengthError] = useState<string | null>(null);
  const [breadthError, setBreadthError] = useState<string | null>(null);
  const [heightError, setHeightError] = useState<string | null>(null);
  const [rateError, setRateError] = useState<string | null>(null);
  const [quantityError, setQuantityError] = useState<string | null>(null);
  const [logisticsError, setLogisticsError] = useState<string | null>(null);
  const [weightError, setWeightError] = useState<string | null>(null);
  const [codError, setCodError] = useState<string | null>(null);
  const [deliveryTypeError, setDeliveryTypeError] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const [errorParams, setErrorParams] = useState<ErrorParams>({
    hsnCode:'', 
    igstDetails:'', 
    sgstDetails: '', 
    cgstDetails:'', 
    discount: '', 
    weight:'', 
    length:'', 
    breadth:'', 
    height: '', 
    cod:'', 
    logistics_cost:''
  });

  useEffect(() => {
    if (currentItem.igst && currentItem.igst !== '') {
      setIsDisabled(true);
      setCurrentItem(prev => ({
        ...prev,
        cgst: '',
        sgst: ''
      }));
    } else if ((currentItem.cgst && currentItem.cgst !== '') || (currentItem.sgst && currentItem.sgst !== '')) {
      setIsDisabled(true);
      setCurrentItem(prev => ({
        ...prev,
        igst: ''
      }));
    } else {
      setIsDisabled(false);
    }
  }, [currentItem.igst, currentItem.cgst, currentItem.sgst]);

  const isNumeric = (v: string) => /^\d*\.?\d*$/.test(v.trim());
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof Omit<Item, 'id'>) => {
    const { value } = e.target;
    
    setCurrentItem(prev => ({
      ...prev,
      [field]: value
    }));

    if (submitError) {
      setSubmitError(null);
    }

    if (["igst", "cgst", "sgst"].includes(field)) {
      const maxValue = 28;
      if (!/^\d*\.?\d*$/.test(value)) {
        setErrorParams((prev) => ({ ...prev, [field + 'Details']: "Only numbers allowed" }));
      } else if ((value.match(/\./g) || []).length > 1) {
        setErrorParams((prev) => ({ ...prev, [field + 'Details']: "Invalid Number" }));
      } else if (parseFloat(value) > maxValue) {
        setErrorParams((prev) => ({ ...prev, [field + 'Details']: `Invalid ${field.replace('Details', '')} %` }));
      } else {
        setErrorParams((prev) => ({ ...prev, [field + 'Details']: "" }));
      }
    }

    if (field === "discount") {
      const maxValue = 100;
      if (!/^\d*\.?\d*$/.test(value)) {
        setErrorParams((prev) => ({ ...prev, [field]: "Only numbers allowed" }));
      } else if ((value.match(/\./g) || []).length > 1) {
        setErrorParams((prev) => ({ ...prev, [field]: "Invalid Number" }));
      } else if (parseFloat(value) > maxValue) {
        setErrorParams((prev) => ({ ...prev, [field]: "Invalid Discount %" }));
      } else {
        setErrorParams((prev) => ({ ...prev, [field]: "" }));
      }
    }

    if (field === "hsnCode") {
      if (value && !/^[0-9]+$/.test(value)) {
        setErrorParams((prev) => ({ ...prev, [field]: "Only number allowed" }));
      } else if (value && value.length > 8) {
        setErrorParams((prev) => ({ ...prev, [field]: "Max 8 digits" }));
      } else {
        setErrorParams((prev) => ({ ...prev, [field]: "" }));
      }
    }

    if (field === "quantity") {
      const maxValue = 1000;
      
      if (value !== '') {
        if (!/^\d+$/.test(value)) {
          setQuantityError("Quantity must be a number");
        } else if (parseFloat(value) === 0) {
          setQuantityError("Quantity must not be zero");
        } else if (parseFloat(value) > maxValue) {
          setQuantityError("Quantity exceeds maximum value.");
        } else {
          setQuantityError(null);
        }
      } else {
        setQuantityError(null);
      }
    }

    if (field === "rate") {
      const maxValue = 100000;
      if (!/^\d*\.?\d*$/.test(value)) {
        setRateError("Rate must be a valid number");
      } else if ((value.match(/\./g) || []).length > 1) {
        setRateError("Only one decimal point allowed");
      } else if (parseFloat(value) > maxValue) {
        setRateError("Rate exceeds maximum value.");
      } else {
        setRateError(null);
      }
    }

    if (field === "skuId") {
      if (value && (!/^[a-zA-Z0-9]+$/.test(value) || value.length > 15)) {
        setFieldError("SKU ID should be alphanumeric and max 15 chars");
      } else {
        setFieldError("");
      }
    }

    if (field === "description") {
      if (/[\*!$><@]/.test(value) || value.length > 128) {
        setFieldError("Description contains invalid characters or too long");
      } else {
        setFieldError("");
      }
    }

    if (field === "item") {
      if (/[\!@$^()\[\]{}'";:/?<>\|]/.test(value) || value.length > 128) {
        setFieldError("Item contains invalid characters or too long");
      } else {
        setFieldError("");
      }
    }
  };

  const handleDimensionChange = (field: 'length' | 'breadth' | 'height', value: string) => {
    setDimensions(prev => ({
      ...prev,
      [field]: value
    }));

    if (value === '') {
      setErrorParams((prev) => ({ ...prev, [field]: "" }));
      return;
    }
    
    if (!isNumeric(value)) {
      setErrorParams((prev) => ({ ...prev, [field]: "Only numbers and one decimal allowed" }));
    } else if ((value.match(/\./g) || []).length > 1) {
      setErrorParams((prev) => ({ ...prev, [field]: "Only one decimal point allowed" }));
    } else {
      setErrorParams((prev) => ({ ...prev, [field]: "" }));
    }

    const num = parseFloat(value);
    if (num > 200) {
      if (field === 'length') setLengthError('Invalid Length');
      if (field === 'breadth') setBreadthError('Invalid Breadth');
      if (field === 'height') setHeightError('Invalid Height');
    } else {
      if (field === 'length') setLengthError(null);
      if (field === 'breadth') setBreadthError(null);
      if (field === 'height') setHeightError(null);
    }
  };

  const handleWeightChange = (value: string) => {
    setWeight(value);
    
    if (value === '') {
      setWeightError('');
      setErrorParams((prev) => ({ ...prev, weight: "" }));
      return;
    }
    
    if (!isNumeric(value)) {
      setWeightError('Only numbers are allowed');
      setErrorParams((prev) => ({ ...prev, weight: "Only numbers allowed" }));
      return;
    }
    
    const num = parseFloat(value);
    if (num > 50) {
      setWeightError('Exceeds Maximum Limit');
      setErrorParams((prev) => ({ ...prev, weight: "Exceeds Maximum Limit" }));
    } else if (num === 0) {
      setWeightError('Weight cannot be 0');
      setErrorParams((prev) => ({ ...prev, weight: "Weight cannot be 0" }));
    } else {
      setWeightError('');
      setErrorParams((prev) => ({ ...prev, weight: "" }));
    }
  };

  const handleLogisticsCostChange = (value: string) => {
    setLogisticsCost(value);
    
    if (value === '') {
      setLogisticsError('');
      setErrorParams((prev) => ({ ...prev, logistics_cost: "" }));
      return;
    }
    
    if (!isNumeric(value)) {
      setLogisticsError('Only numbers are allowed');
      setErrorParams((prev) => ({ ...prev, logistics_cost: "Only numbers allowed" }));
      return;
    }
    
    const num = parseFloat(value);
    if (num > 100000) {
      setLogisticsError('Value exceeds Maximum Limit');
      setErrorParams((prev) => ({ ...prev, logistics_cost: "Value exceeds Maximum Limit" }));
    } else {
      setLogisticsError('');
      setErrorParams((prev) => ({ ...prev, logistics_cost: "" }));
    }
  };

  const calculateItemTotal = (item: Omit<Item, 'id'>) => {
    const rateValue = parseFloat(item.rate || '0');
    const quantityValue = parseFloat(item.quantity || '0');
    const igstValue = parseFloat(item.igst || '0');
    const cgstValue = parseFloat(item.cgst || '0');
    const sgstValue = parseFloat(item.sgst || '0');
    const discountValue = parseFloat(item.discount || '0');
    
    const subTotal = rateValue * quantityValue;
    const discountAmount = subTotal * discountValue / 100;
    const subTotalWithDiscount = subTotal - discountAmount;
    const totalGst = (igstValue + cgstValue + sgstValue) / 100 * subTotalWithDiscount;
    const total = subTotalWithDiscount + totalGst;
    
    return total.toFixed(2);
  };

  const calculateGrandTotal = () => {
    let total = 0;
    
    items.forEach(item => {
      total += parseFloat(calculateItemTotal(item));
    });
    
    if (currentItem.rate && currentItem.quantity) {
      total += parseFloat(calculateItemTotal(currentItem));
    }
    
    total += parseFloat(logisticsCost || '0');
    
    setGrandTotal(total.toFixed(2));
  };

  useEffect(() => {
    calculateGrandTotal();
  }, [items, currentItem, logisticsCost]);
  const validateItems = () => {
    const hasItemsInList = items.length > 0;
    const hasCurrentItem = currentItem.item && currentItem.description && currentItem.rate && currentItem.quantity;
    
    if (!hasItemsInList && !hasCurrentItem) {
      setSubmitError('At least 1 item should be added');
      return false;
    }
    
    for (const item of items) {
      if (!item.item || !item.description || !item.rate || !item.quantity) {
        setSubmitError('All fields (Item, Description, Rate, Quantity) are required for each item');
        return false;
      }
    }
    
    if (hasCurrentItem && !hasItemsInList) {
      if (!currentItem.item || !currentItem.description || !currentItem.rate || !currentItem.quantity) {
        setSubmitError('All fields (Item, Description, Rate, Quantity) are required for the current item');
        return false;
      }
    }
    
    setSubmitError(null);
    return true;
  };

  const handleAddItem = () => {
    if (!currentItem.item || !currentItem.description || !currentItem.rate || !currentItem.quantity) {
      setFieldError("All fields are required.");
      return;
    }
    
    if (currentItem.cgst && !currentItem.sgst) {
      setGstError("SGST Can't be empty");
      return;
    } else if (!currentItem.cgst && currentItem.sgst) {
      setGstError("CGST Can't be empty");
      return;
    }
    
    if (rateError || quantityError || fieldError || gstError) {
      return;
    }
     
    const newItem = {
      ...currentItem,
      id: Date.now().toString(),
      total: calculateItemTotal(currentItem)
    };
    
    setItems([...items, newItem]);
    setCurrentItem({
      skuId: '',
      item: '',
      description: '',
      rate: '',
      quantity: '',
      total: '0.00',
      hsnCode: '',
      igst: '',
      cgst: '',
      sgst: '',
      discount: ''
    });
    setShowGst(false);
    setFieldError('');
    setGstError(null);
  };

  const handleDeleteItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleSubmit = () => {
    if (!validateItems()) {
      return;
    }

    if (!weight) {
      setWeightError('Weight is required');
      return;
    } else {
      setWeightError(null);
    }

    if (!cod || cod === 'Select') {
      setCodError('COD selection is required');
      return;
    } else {
      setCodError(null);
    }

    if (!deliveryType || deliveryType === 'Select') {
      setDeliveryTypeError('Delivery type is required');
      return;
    } else {
      setDeliveryTypeError(null);
    }

    if (currentItem.cgst && !currentItem.sgst) {
      setGstError("SGST Can't be empty");
      return;
    } else if (!currentItem.cgst && currentItem.sgst) {
      setGstError("CGST Can't be empty");
      return;
    }

    let updatedItems = [...items];
    const hasCurrentItem = currentItem.item && currentItem.description && currentItem.rate && currentItem.quantity;
    
    if (hasCurrentItem && items.length === 0) {
      updatedItems.push({
        ...currentItem,
        id: Date.now().toString(),
        total: calculateItemTotal(currentItem)
      });
    }

    if (updatedItems.length === 0) {
      toast.error('At least one item is required.');
      return;
    }

    if (rateError || quantityError || lengthError || breadthError || heightError || logisticsError || gstError || weightError || codError || fieldError) {
      return;
    }

    if (grandTotal === '0.00') {
      toast.error("Grand total cannot be zero");
      return;
    }

    const firstItem = updatedItems[0];
    const orderDetails = {
      ...orderData,
      item_name: firstItem.item,
      item_description: firstItem.description,
      price: firstItem.rate,
      quantity: firstItem.quantity,
      cgst_amount: firstItem.cgst ? parseFloat(firstItem.cgst) : 0,
      sgst_amount: firstItem.sgst ? parseFloat(firstItem.sgst) : 0,
      igst_amount: firstItem.igst ? parseFloat(firstItem.igst) : 0,
      discount_amount: firstItem.discount ? parseFloat(firstItem.discount) : 0,
      hsn_code: firstItem.hsnCode || "",
      sku_id: firstItem.skuId || "",
      weight,
      deliveryType,
      cod: cod === 'yes' ? 'yes' : 'no',
      delivery_type: deliveryType,
      logistics_cost: logisticsCost,
      length: dimensions.length,
      breadth: dimensions.breadth,
      height: dimensions.height,
      grandTotal
    };

    if (updatedItems.length > 1) {
      const additionalItems: Record<string, any> = {};

      updatedItems.slice(1).forEach((item, index) => {
        const itemKey = `item${index + 1}`; 
        additionalItems[itemKey] = {
          item_name: item.item,
          item_description: item.description,
          price: item.rate,
          quantity: item.quantity,
          cgst_amount: item.cgst ? parseFloat(item.cgst) : 0,
          sgst_amount: item.sgst ? parseFloat(item.sgst) : 0,
          igst_amount: item.igst ? parseFloat(item.igst) : 0,
          discount_amount: item.discount ? parseFloat(item.discount) : 0,
          hsn_code: item.hsnCode || "",
          sku_id: item.skuId || ""
        };
      });

      orderDetails.additional_items = JSON.stringify(additionalItems);
    }
    onSubmit(orderDetails);
  };

  const codOptions = [
    {id:'yes', label:'COD'},
    {id:'no', label:"Prepaid"}
  ];

  const deliveryTypeOptions = [
    {id:'delivery', label:'Delivery'},
    {id:'pickup', label:"PickUp"}
  ];

  const tableCellStyle = {
    border: '1px solid #d0d0d0',
    padding: '8px',
    backgroundColor: '#ffffff',
    boxShadow: 'inset 1px 1px 0px #f0f0f0, inset -1px -1px 0px #e0e0e0',
    verticalAlign: 'top',
    wordWrap: 'break-word',
    maxWidth: '200px'
  };

  const wrappedCellStyle = {
    ...tableCellStyle,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    overflowWrap: 'break-word'
  };

  const tableContainerStyle = {
    border: '1px solid #b0b0b0',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
    overflow: 'hidden',
    backgroundColor: '#fafafa'
  };
  
  const tableHeaderStyle = {
    ...tableCellStyle,
    backgroundColor: '#e8e8e8',
    fontWeight: 'bold',
    boxShadow: 'inset 1px 1px 0px #f8f8f8, inset -1px -1px 0px #d0d0d0'
  };

  return (
    <Box>

      <DialogContent>
        {submitError && (
          <Typography color="error" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
            {submitError}
          </Typography>
        )}
        
        <TableContainer component={Paper} sx={tableContainerStyle}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderStyle}>Sl No</TableCell>
                <TableCell sx={tableHeaderStyle}>SKU ID</TableCell>
                <TableCell sx={tableHeaderStyle}>Item</TableCell>
                <TableCell sx={tableHeaderStyle}>Description</TableCell>
                <TableCell sx={tableHeaderStyle}>Rate</TableCell>
                <TableCell sx={tableHeaderStyle}>Quantity</TableCell>
                <TableCell sx={tableHeaderStyle}>Total</TableCell>
                <TableCell sx={tableHeaderStyle}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={tableCellStyle}>{items.length + 1}</TableCell>
                <TableCell sx={tableCellStyle}>
                  <TextField
                    size="small"
                    value={currentItem.skuId}
                    onChange={(e) => handleInputChange(e, 'skuId')}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: 'white',
                        '& fieldset': { border: 'none' }
                      } 
                    }}
                    placeholder="Optional"
                    error={!!fieldError && fieldError.includes("SKU ID")}
                  />
                </TableCell>
                <TableCell sx={wrappedCellStyle}>
                  <TextField
                    size="small"
                    value={currentItem.item}
                    onChange={(e) => handleInputChange(e, 'item')}
                    required
                    fullWidth
                    multiline
                    maxRows={3}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: 'white',
                        '& fieldset': { border: 'none' },
                        padding: 0
                      },
                      '& .MuiInputBase-input': {
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        padding: '0px 0px'
                      }
                    }}
                    error={!!fieldError && fieldError.includes("Item")}
                  />
                </TableCell>
                <TableCell sx={wrappedCellStyle}>
                  <TextField
                    size="small"
                    value={currentItem.description}
                    onChange={(e) => handleInputChange(e, 'description')}
                    required
                    fullWidth
                    multiline
                    maxRows={3}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: 'white',
                        '& fieldset': { border: 'none' },
                        padding: 0
                      },
                      '& .MuiInputBase-input': {
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        padding: '0px 0px'
                      }
                    }}
                    error={!!fieldError && fieldError.includes("Description")}
                  />
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  <TextField
                    size="small"
                    type="text"
                    value={currentItem.rate}
                    onChange={(e) => handleInputChange(e, 'rate')}
                    required
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: 'white',
                        '& fieldset': { border: 'none' }
                      } 
                    }}
                    error={!!rateError}
                    helperText={rateError}
                  />
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  <TextField
                    size="small"
                    type="text"
                    value={currentItem.quantity}
                    onChange={(e) => handleInputChange(e, 'quantity')}
                    required
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: 'white',
                        '& fieldset': { border: 'none' }
                      } 
                    }}
                    error={!!quantityError}
                    helperText={quantityError}
                  />
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  <TextField
                    size="small"
                    value={calculateItemTotal(currentItem)}
                    disabled
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: '#f5f5f5',
                        '& fieldset': { border: 'none' }
                      } 
                    }}
                  />
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  <IconButton onClick={() => setShowGst(!showGst)}>
                    <AddIcon />
                  </IconButton>
                </TableCell>
              </TableRow>

              {showGst && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ padding: 0, border: 'none' }}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#f0f0f0', 
                      border: '1px solid #d0d0d0', 
                      borderTop: 'none',
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <Grid container spacing={2}>
                        <Grid item xs={3}>
                          <TextField
                            label="HSN Code"
                            size="small"
                            fullWidth
                            value={currentItem.hsnCode || ''}
                            onChange={(e) => handleInputChange(e, 'hsnCode')}
                            error={!!errorParams.hsnCode}
                            helperText={errorParams.hsnCode}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            label="IGST %"
                            size="small"
                            fullWidth
                            type="number"
                            value={currentItem.igst || ''}
                            onChange={(e) => handleInputChange(e, 'igst')}
                            disabled={isDisabled && (currentItem.cgst !== '' || currentItem.sgst !== '')}
                            error={!!errorParams.igstDetails}
                            helperText={errorParams.igstDetails}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            label="CGST %"
                            size="small"
                            fullWidth
                            type="number"
                            value={currentItem.cgst || ''}
                            onChange={(e) => handleInputChange(e, 'cgst')}
                            disabled={isDisabled && currentItem.igst !== ''}
                            error={!!errorParams.cgstDetails}
                            helperText={errorParams.cgstDetails}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            label="SGST %"
                            size="small"
                            fullWidth
                            type="number"
                            value={currentItem.sgst || ''}
                            onChange={(e) => handleInputChange(e, 'sgst')}
                            disabled={isDisabled && currentItem.igst !== ''}
                            error={!!errorParams.sgstDetails}
                            helperText={errorParams.sgstDetails}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            label="Discount %"
                            size="small"
                            fullWidth
                            type="number"
                            value={currentItem.discount || ''}
                            onChange={(e) => handleInputChange(e, 'discount')}
                            error={!!errorParams.discount}
                            helperText={errorParams.discount}
                          />
                        </Grid>
                      </Grid>
                      {gstError && <Typography color="error" sx={{ mt: 1 }}>{gstError}</Typography>}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              
              {items.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell sx={tableCellStyle}>{index + 1}</TableCell>
                  <TableCell sx={tableCellStyle}>{item.skuId}</TableCell>
                  <TableCell sx={wrappedCellStyle}>{item.item}</TableCell>
                  <TableCell sx={wrappedCellStyle}>{item.description}</TableCell>
                  <TableCell sx={tableCellStyle}>{item.rate}</TableCell>
                  <TableCell sx={tableCellStyle}>{item.quantity}</TableCell>
                  <TableCell sx={tableCellStyle}>{item.total}</TableCell>
                  <TableCell sx={tableCellStyle}>
                    <IconButton onClick={() => handleDeleteItem(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {fieldError && <Typography color="error" sx={{ mt: 1 }}>{fieldError}</Typography>}

        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'right', justifyContent: 'right', gap: 2, mb: 2 }}>
            <Dropdown 
              options={codOptions}
              selectedValue={cod}
              handleChange={(e) => {
                setCod(e.target.value as string);
                if (e.target.value !== 'Select') {
                  setCodError(null);
                }
              }}
              width="120px"
              height="50px"
              placeholder='Payment Type'
              label={'Payment Type'}
              error={!!codError}
              helperText={codError}
            />
            <Dropdown 
              options={deliveryTypeOptions}
              selectedValue={deliveryType}
              handleChange={(e) => {
                setDeliveryType(e.target.value as string);
                if (e.target.value !== 'Select') {
                  setDeliveryTypeError(null);
                }
              }}
              width="160px"
              height="50px"
              placeholder='Delivery Type'
              label="Delivery Type"
              error={!!deliveryTypeError}
              helperText={deliveryTypeError}
            />
            <Button 
              variant="contained" 
              className='custom-button'
              startIcon={<AddIcon />}
              onClick={handleAddItem}
            >
              Add Item
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Grand Total: ₹{grandTotal}
          </Typography>
        </Box>

        <Typography variant="h6" sx={{ mt: 0, mb: 0, fontSize:'1rem', fontWeight:'bold' }}>
          Package Details
        </Typography>
        
        <Grid container spacing={2} sx={{ mt: 2, paddingTop:0 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Weight (kg)"
              fullWidth
              size="small"
              value={weight}
              onChange={(e) => handleWeightChange(e.target.value)}
              error={!!weightError || !!errorParams.weight}
              helperText={weightError || errorParams.weight}
              InputProps={{
                endAdornment: <InputAdornment position="end">kg</InputAdornment>,
              }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="No: of Crates"
              fullWidth
              size="small"
              value={crates}
              onChange={(e) => setCrates(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Logistics Cost"
              fullWidth
              size="small"
              value={logisticsCost}
              onChange={(e) => handleLogisticsCostChange(e.target.value)}
              error={!!logisticsError || !!errorParams.logistics_cost}
              helperText={logisticsError || errorParams.logistics_cost}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Length"
              fullWidth
              size="small"
              value={dimensions.length}
              onChange={(e) => handleDimensionChange('length', e.target.value)}
              error={!!lengthError || !!errorParams.length}
              helperText={lengthError || errorParams.length}
              InputProps={{
                endAdornment: <InputAdornment position="end">cm</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Breadth"
              fullWidth
              size="small"
              value={dimensions.breadth}
              onChange={(e) => handleDimensionChange('breadth', e.target.value)}
              error={!!breadthError || !!errorParams.breadth}
              helperText={breadthError || errorParams.breadth}
              InputProps={{
                endAdornment: <InputAdornment position="end">cm</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Height"
              fullWidth
              size="small"
              value={dimensions.height}
              onChange={(e) => handleDimensionChange('height', e.target.value)}
              error={!!heightError || !!errorParams.height}
              helperText={heightError || errorParams.height}
              InputProps={{
                endAdornment: <InputAdornment position="end">cm</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', px: 3 }}>
        <Button
          onClick={onCancel}
          className="custom-cancel-button"
          style={{ marginRight: '10px' }}
        >
          Cancel
        </Button>
        <Button 
          className="custom-button"
          onClick={handleSubmit}
        >
          Submit Order
        </Button>
      </DialogActions>
    </Box>
  );
};

export default ItemDetailsForm;